import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { PRODUCT_TO_INTERNAL_REF } from 'constants/products.js';
import { useOrgUser } from 'contexts/OrgUserContext.jsx';
import { useProductDataOutsideRoutes } from 'hooks/index.js';

const getQueryKey = (orgId, userId, selectedProduct, type) => ['user-notifications', orgId, userId, selectedProduct, type];

const useUserNotifications = ({ pollIntervalMs = 30000, type = 'bamhub-toast', onError } = {}) => {
  const firebase = useFirebase();
  const { isSignedIn, userId } = useOrgUser();
  const { orgId, selectedProduct: selectedPublicProductRef } = useProductDataOutsideRoutes();
  const selectedProduct = PRODUCT_TO_INTERNAL_REF[selectedPublicProductRef];

  const queryClient = useQueryClient();
  const queryKey = getQueryKey(orgId, userId, selectedProduct, type);

  const markNotificationAsSeen = useMutation({
    mutationFn: async ({ notificationId }) => {
      const agent = await firebase.getAgent();
      queryClient.removeQueries(queryKey);
      const response = await agent.put(`/o/${orgId}/users/${userId}/notifications/${notificationId}/seen`, { seen: true });
      if (response?.status === 204) {
        queryClient.invalidateQueries(queryKey);
      }
    },
  });

  const response = useQuery({
    enabled: !!(isSignedIn && orgId && userId && selectedProduct),
    queryKey,
    queryFn: async () => {
      const agent = await firebase.getAgent();
      const params = { product: selectedProduct, seen: false, type, pageSize: 20 };
      const response = await agent.get(`/o/${orgId}/users/${userId}/notifications`, params);

      const { body, error } = response;
      if (error) {
        return onError?.(error);
      }
      const { data } = body;

      return data;
    },
    onError: (error, variables) => {
      console.log(error);
      onError?.(error, variables);
    },
    refetchInterval: pollIntervalMs,
  });

  return { ...response, markNotificationAsSeen };
};

export default useUserNotifications;
